import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail,
  RefreshCcw,
  Package,
  Filter,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Truck,
  ExternalLink,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import AdminBreadcrumb from '@/components/AdminBreadcrumb';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Unified status pipeline — every real status a row can be in, across both
// order kinds. Filter chips read from this list; the label + dot for a given
// status is looked up here for status pills too.
const STATUSES = [
  { value: 'awaiting_payment', label: 'Awaiting payment', dot: '#D4AF37', kinds: ['paid'] },
  { value: 'pending',          label: 'Pending',          dot: '#D4AF37', kinds: ['custom'] },
  { value: 'confirmed',        label: 'Paid · new',       dot: '#84A98C', kinds: ['paid'] },
  { value: 'in-progress',      label: 'In progress',      dot: '#52796F', kinds: ['paid', 'custom'] },
  { value: 'shipped',          label: 'Shipped',          dot: '#354F52', kinds: ['paid', 'custom'] },
  { value: 'completed',        label: 'Completed',        dot: '#354F52', kinds: ['paid', 'custom'] },
  { value: 'cancelled',        label: 'Cancelled',        dot: '#BC4749', kinds: ['paid', 'custom'] },
];

// Which statuses each row's inline "Set status" action button should offer.
const PAID_ACTION_STATUSES = STATUSES.filter(
  (s) => s.kinds.includes('paid') && s.value !== 'awaiting_payment' && s.value !== 'shipped',
);
const CUSTOM_ACTION_STATUSES = STATUSES.filter((s) => s.kinds.includes('custom'));

const STATUS_LOOKUP = STATUSES.reduce((acc, s) => ({ ...acc, [s.value]: s }), {});
const CARRIERS = ['ACS Couriers', 'Cyprus Post', 'DHL', 'UPS', 'Royal Mail'];

const defaultStatusForKind = (kind) => (kind === 'paid' ? 'confirmed' : 'pending');

function StatusPill({ status }) {
  const meta = STATUS_LOOKUP[status] || { label: status, dot: '#84A98C' };
  return (
    <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest">
      <span className="w-2 h-2 rounded-full" style={{ background: meta.dot }} aria-hidden="true" />
      <span style={{ color: meta.dot }}>{meta.label}</span>
    </span>
  );
}

function KindBadge({ kind }) {
  const isPaid = kind === 'paid';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] rounded-sm ${
        isPaid
          ? 'bg-[#D4AF37]/15 text-[#8B7500] border border-[#D4AF37]/40'
          : 'bg-[#52796F]/10 text-[#52796F] border border-[#52796F]/30'
      }`}
      data-testid={`kind-badge-${kind}`}
    >
      {isPaid ? 'Paid' : 'Custom'}
    </span>
  );
}

export default function OrdersAdminPage() {
  const [paidOrders, setPaidOrders] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [paid, custom] = await Promise.all([
        axios.get(`${API}/orders`, { params: { limit: 200 } }),
        axios.get(`${API}/orders/custom`, { params: { limit: 200 } }),
      ]);
      setPaidOrders(Array.isArray(paid.data) ? paid.data : []);
      setCustomOrders(Array.isArray(custom.data) ? custom.data : []);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Merge both lists into one, tagged with _kind, sorted newest-first.
  const allOrders = useMemo(() => {
    const paidTagged = paidOrders.map((o) => ({ ...o, _kind: 'paid' }));
    const customTagged = customOrders.map((o) => ({ ...o, _kind: 'custom' }));
    return [...paidTagged, ...customTagged].sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return db - da;
    });
  }, [paidOrders, customOrders]);

  const applyOrderUpdate = (kind, updated) => {
    if (kind === 'paid') {
      setPaidOrders((prev) => prev.map((o) => (o.id === updated.id ? { ...o, ...updated } : o)));
    } else {
      setCustomOrders((prev) => prev.map((o) => (o.id === updated.id ? { ...o, ...updated } : o)));
    }
  };

  const removeOrder = async (kind, id) => {
    const label =
      kind === 'paid'
        ? 'this PayPal order'
        : 'this custom-form order';
    if (!window.confirm(`Delete ${label}? This is permanent and cannot be undone.`)) return;
    try {
      const endpoint = kind === 'paid' ? `${API}/orders/${id}` : `${API}/orders/custom/${id}`;
      await axios.delete(endpoint);
      if (kind === 'paid') {
        setPaidOrders((prev) => prev.filter((o) => o.id !== id));
      } else {
        setCustomOrders((prev) => prev.filter((o) => o.id !== id));
      }
      toast.success('Order deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete order');
    }
  };

  return (
    <div className="pt-28 pb-16 min-h-screen bg-[#F5F5F0]" data-testid="orders-admin-page">
      <div className="container-custom px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
        <AdminBreadcrumb current="Orders" />

        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-caption text-[#84A98C] mb-2">Admin</p>
            <h1 className="heading-display text-3xl md:text-4xl text-[#354F52]">Orders</h1>
            <p className="text-[#52796F] mt-2 text-sm max-w-2xl">
              Every order in one list — Bracelet Builder / Custom form orders and
              PayPal cart checkouts, sorted newest first. Click a row to expand and
              update its status.
            </p>
          </div>
          <Button
            type="button"
            onClick={fetchAll}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="orders-admin-refresh"
          >
            <RefreshCcw className="w-4 h-4" strokeWidth={1.5} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-[#84A98C]">Loading orders…</p>
        ) : (
          <UnifiedOrdersList orders={allOrders} onChange={applyOrderUpdate} onDelete={removeOrder} />
        )}
      </div>
    </div>
  );
}

/* ───────── Unified list ───────── */
function UnifiedOrdersList({ orders, onChange, onDelete }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [expanded, setExpanded] = useState({});

  const visible = useMemo(() => {
    return orders.filter((o) => {
      const status = o.status || defaultStatusForKind(o._kind);
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (kindFilter !== 'all' && o._kind !== kindFilter) return false;
      return true;
    });
  }, [orders, statusFilter, kindFilter]);

  const statusCounts = useMemo(() => {
    const c = { all: orders.length };
    STATUSES.forEach((s) => {
      c[s.value] = orders.filter(
        (o) => (o.status || defaultStatusForKind(o._kind)) === s.value,
      ).length;
    });
    return c;
  }, [orders]);

  const kindCounts = useMemo(
    () => ({
      all: orders.length,
      custom: orders.filter((o) => o._kind === 'custom').length,
      paid: orders.filter((o) => o._kind === 'paid').length,
    }),
    [orders],
  );

  const toggleExpand = (row) =>
    setExpanded((prev) => ({ ...prev, [`${row._kind}:${row.id}`]: !prev[`${row._kind}:${row.id}`] }));

  return (
    <>
      {/* Kind filter */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="orders-kind-filters">
        <span className="text-caption text-[#84A98C] mr-1">Type:</span>
        <Chip
          active={kindFilter === 'all'}
          onClick={() => setKindFilter('all')}
          label="All"
          count={kindCounts.all}
          testId="kind-filter-all"
        />
        <Chip
          active={kindFilter === 'custom'}
          onClick={() => setKindFilter('custom')}
          label="Custom"
          count={kindCounts.custom}
          testId="kind-filter-custom"
        />
        <Chip
          active={kindFilter === 'paid'}
          onClick={() => setKindFilter('paid')}
          label="Paid (PayPal)"
          count={kindCounts.paid}
          testId="kind-filter-paid"
        />
      </div>

      {/* Status filter */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="orders-status-filters">
        <Filter className="w-4 h-4 text-[#84A98C]" strokeWidth={1.5} />
        <Chip
          active={statusFilter === 'all'}
          onClick={() => setStatusFilter('all')}
          label="All"
          count={statusCounts.all}
          testId="status-filter-all"
        />
        {STATUSES.map((s) => (
          <Chip
            key={s.value}
            active={statusFilter === s.value}
            onClick={() => setStatusFilter(s.value)}
            label={s.label}
            count={statusCounts[s.value] || 0}
            dot={s.dot}
            testId={`status-filter-${s.value}`}
          />
        ))}
      </div>

      {visible.length === 0 ? (
        <EmptyState
          message={
            orders.length === 0
              ? 'No orders yet — they will appear here as customers check out or submit the custom form.'
              : 'No orders match the current filters.'
          }
        />
      ) : (
        <div
          className="bg-white rounded-sm border border-[#CAD2C5]/40 overflow-hidden"
          data-testid="orders-unified-list"
        >
          {visible.map((o) => (
            <OrderRow
              key={`${o._kind}:${o.id}`}
              order={o}
              expanded={!!expanded[`${o._kind}:${o.id}`]}
              onToggle={() => toggleExpand(o)}
              onChange={onChange}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </>
  );
}

/* ───────── Row (dispatches to paid vs. custom body) ───────── */
function OrderRow({ order, expanded, onToggle, onChange, onDelete }) {
  const created = order.created_at ? new Date(order.created_at) : null;
  const dateStr = created ? created.toLocaleDateString('en-GB') : '—';
  const timeStr = created
    ? created.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : '';
  const status = order.status || defaultStatusForKind(order._kind);

  // Title + subtitle differ per kind
  const title =
    order._kind === 'paid'
      ? `#${(order.id || '').slice(0, 8).toUpperCase()}`
      : (order.custom_text || '').toUpperCase() || '—';
  const customerName =
    order._kind === 'paid' ? order.customer_name : order.name;
  const customerEmail =
    order._kind === 'paid' ? order.customer_email : order.email;
  const priceLabel =
    order._kind === 'paid'
      ? `€${(order.total || 0).toFixed(2)}`
      : `€${(order.price || 0).toFixed(2)}`;
  const middleInfo =
    order._kind === 'paid'
      ? (() => {
          const cnt = (order.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
          return `${cnt} item${cnt === 1 ? '' : 's'}`;
        })()
      : `${order.bracelet_style || '—'}${order.clasp ? ` · ${order.clasp}` : ''}`;

  return (
    <div
      className="border-b border-[#CAD2C5]/40 last:border-b-0"
      data-testid={`order-row-${order._kind}-${order.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-[#FAF9F6] transition-colors"
        data-testid={`order-toggle-${order._kind}-${order.id}`}
      >
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-4 sm:gap-6 items-center flex-1 min-w-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <KindBadge kind={order._kind} />
            </div>
            <p className="font-serif text-[#354F52] text-base truncate">{title}</p>
            <p className="text-xs text-[#84A98C] truncate">
              {dateStr}
              {timeStr ? ` · ${timeStr}` : ''}
            </p>
          </div>
          <div className="min-w-0">
            <p className="text-sm text-[#354F52] truncate">{customerName || '—'}</p>
            <p className="text-xs text-[#84A98C] truncate">{customerEmail || ''}</p>
          </div>
          <div className="text-xs text-[#52796F] tracking-widest uppercase whitespace-nowrap hidden sm:block">
            {middleInfo}
          </div>
          <div className="text-right whitespace-nowrap">
            <p className="font-serif text-[#354F52]">{priceLabel}</p>
            <StatusPill status={status} />
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-[#84A98C] flex-shrink-0" strokeWidth={1.5} />
        ) : (
          <ChevronDown className="w-4 h-4 text-[#84A98C] flex-shrink-0" strokeWidth={1.5} />
        )}
      </button>

      {expanded && (
        <div
          className="px-5 pb-5 bg-[#FAF9F6]"
          data-testid={`order-details-${order._kind}-${order.id}`}
        >
          {order._kind === 'paid' ? (
            <PaidOrderBody order={order} onChange={(updated) => onChange('paid', updated)} />
          ) : (
            <CustomOrderBody order={order} onChange={(updated) => onChange('custom', updated)} />
          )}
          <div className="mt-6 pt-4 border-t border-[#CAD2C5]/40 flex justify-end">
            <button
              type="button"
              onClick={() => onDelete(order._kind, order.id)}
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#BC4749] hover:text-[#8a2830] transition-colors"
              data-testid={`order-delete-${order._kind}-${order.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              Delete order
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────── Paid (PayPal) body ───────── */
function PaidOrderBody({ order, onChange }) {
  return (
    <>
      <div className="pt-3">
        <p className="text-caption text-[#84A98C] mb-2">Items</p>
        <div className="space-y-2">
          {(order.items || []).map((it, idx) => (
            <div key={idx} className="flex justify-between text-sm text-[#354F52]">
              <span className="truncate pr-4">
                {it.product_name}
                {it.size_cm ? ` · ${it.size_cm} cm` : ''}
                {it.clasp ? ` · ${it.clasp}` : ''}
                {it.custom_text ? ` · "${it.custom_text}"` : ''}
                {' '}× {it.quantity}
              </span>
              <span className="text-[#D4AF37] whitespace-nowrap">
                €{(it.subtotal || 0).toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-5">
        <Detail label="Ship to">
          <span className="whitespace-pre-line">{order.shipping_address || '—'}</span>
        </Detail>
        <Detail label="Phone">{order.customer_phone || '—'}</Detail>
        {order.notes && <Detail label="Customer notes">{order.notes}</Detail>}
        {order.tracking_number && (
          <Detail label="Tracking">
            <span className="font-mono">{order.tracking_number}</span>
            {' '}
            <span className="text-[#84A98C]">· {order.carrier || 'ACS Couriers'}</span>
            {order.tracking_url && (
              <a
                href={order.tracking_url}
                target="_blank"
                rel="noreferrer"
                className="ml-2 inline-flex items-center gap-1 text-[#D4AF37] text-xs"
              >
                track <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </Detail>
        )}
      </div>

      <ShipForm order={order} onChange={onChange} />
    </>
  );
}

function ShipForm({ order, onChange }) {
  const isShipped = order.status === 'shipped';
  const [carrier, setCarrier] = useState(order.carrier || 'ACS Couriers');
  const [tracking, setTracking] = useState(order.tracking_number || '');
  const [sending, setSending] = useState(false);

  const submitShipped = async () => {
    if (!tracking.trim()) {
      toast.error('Add a tracking number first');
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.patch(`${API}/orders/${order.id}`, {
        status: 'shipped',
        carrier: carrier.trim(),
        tracking_number: tracking.trim(),
        send_email: true,
      });
      onChange(data);
      toast.success(
        data._email_sent
          ? 'Marked shipped & tracking email sent ✨'
          : 'Marked shipped — email not sent (check Resend config)',
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update order');
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    if (status === order.status) return;
    setSending(true);
    try {
      const { data } = await axios.patch(`${API}/orders/${order.id}`, {
        status,
        send_email: false,
      });
      onChange(data);
      toast.success(`Order marked ${status.replace('-', ' ')}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update order');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-5 border-t border-[#CAD2C5]/40 pt-5">
      <p className="text-caption text-[#84A98C] mb-3">Update status</p>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {PAID_ACTION_STATUSES.map((s) => {
          const isCurrent = order.status === s.value;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => setStatus(s.value)}
              disabled={isCurrent || sending}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs uppercase tracking-widest transition-colors ${
                isCurrent
                  ? 'bg-[#354F52] text-[#FAF9F6] cursor-default'
                  : 'bg-white text-[#52796F] hover:bg-[#354F52]/5 border border-[#CAD2C5]'
              }`}
              data-testid={`paid-status-${order.id}-${s.value}`}
            >
              {isCurrent && <CheckCircle className="w-3 h-3" strokeWidth={1.5} />}
              <span className="w-2 h-2 rounded-full" style={{ background: s.dot }} aria-hidden="true" />
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-[#CAD2C5]/50 rounded-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Truck className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
          <p className="font-serif text-[#354F52]">
            {isShipped ? 'Shipped — resend tracking email' : 'Mark shipped & send tracking email'}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-3 items-end">
          <label className="block text-xs">
            <span className="text-[#84A98C] uppercase tracking-widest text-[11px]">Carrier</span>
            <select
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              disabled={sending}
              className="mt-1 w-full bg-[#FAF9F6] border border-[#CAD2C5] text-sm text-[#354F52] px-3 py-2 rounded-sm focus:border-[#D4AF37] outline-none"
              data-testid={`ship-carrier-${order.id}`}
            >
              {CARRIERS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs">
            <span className="text-[#84A98C] uppercase tracking-widest text-[11px]">
              Tracking number
            </span>
            <Input
              type="text"
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              placeholder="e.g. ABCD1234567CY"
              disabled={sending}
              className="mt-1 font-mono"
              data-testid={`ship-tracking-${order.id}`}
            />
          </label>
          <Button
            type="button"
            onClick={submitShipped}
            disabled={sending || !tracking.trim()}
            className="bg-[#354F52] hover:bg-[#52796F] whitespace-nowrap"
            data-testid={`ship-submit-${order.id}`}
          >
            <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
            {sending
              ? 'Sending…'
              : isShipped
              ? 'Resend email'
              : 'Mark shipped & email'}
          </Button>
        </div>
        {order.tracking_email_sent_at && (
          <p className="text-[11px] text-[#84A98C] mt-3">
            Last tracking email sent{' '}
            {new Date(order.tracking_email_sent_at).toLocaleString('en-GB')}
          </p>
        )}
      </div>
    </div>
  );
}

/* ───────── Custom-form body ───────── */
function CustomOrderBody({ order, onChange }) {
  const [saving, setSaving] = useState(false);

  const setStatus = async (next) => {
    if ((order.status || 'pending') === next) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/orders/custom/${order.id}`, { status: next });
      onChange({ id: order.id, status: next });
      toast.success(`Order marked ${next}`);
    } catch {
      toast.error('Could not update status');
    } finally {
      setSaving(false);
    }
  };

  const shippingAddressBlock = [
    [order.shipping_first_name, order.shipping_last_name].filter(Boolean).join(' '),
    order.shipping_address_line1,
    order.shipping_address_line2,
    [order.shipping_postal_code, order.shipping_city].filter(Boolean).join(' '),
    order.shipping_country,
  ].filter(Boolean).join('\n');

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-3">
        <Detail label="Bracelet word">{order.custom_text}</Detail>
        <Detail label="Morse code">
          <span className="font-mono text-[#52796F] tracking-widest">{order.morse_code}</span>
        </Detail>
        <Detail label="Style">{order.bracelet_style || '—'}</Detail>
        <Detail label="Clasp">{order.clasp || '—'}</Detail>
        <Detail label="Quantity">{order.quantity}</Detail>
        <Detail label="Order ID">
          <span className="font-mono text-xs text-[#84A98C] select-all">{order.id}</span>
        </Detail>
        {shippingAddressBlock && (
          <Detail label="Ship to">
            <span className="whitespace-pre-line">{shippingAddressBlock}</span>
          </Detail>
        )}
        {order.phone && <Detail label="Phone">{order.phone}</Detail>}
      </div>

      {order.special_instructions && (
        <div className="mt-5">
          <p className="text-caption text-[#84A98C] mb-2">Maker&rsquo;s instructions</p>
          <pre
            className="whitespace-pre-wrap bg-white border border-[#CAD2C5]/50 rounded-sm p-4 text-sm text-[#354F52] font-sans"
            data-testid={`order-instructions-${order.id}`}
          >
            {order.special_instructions}
          </pre>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <p className="text-caption text-[#84A98C] mr-2">Set status:</p>
        {CUSTOM_ACTION_STATUSES.map((s) => {
          const isCurrent = (order.status || 'pending') === s.value;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => setStatus(s.value)}
              disabled={isCurrent || saving}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs uppercase tracking-widest transition-colors ${
                isCurrent
                  ? 'bg-[#354F52] text-[#FAF9F6] cursor-default'
                  : 'bg-white text-[#52796F] hover:bg-[#354F52]/5 border border-[#CAD2C5]'
              }`}
              data-testid={`order-status-${order.id}-${s.value}`}
            >
              {isCurrent && <CheckCircle className="w-3 h-3" strokeWidth={1.5} />}
              <span className="w-2 h-2 rounded-full" style={{ background: s.dot }} aria-hidden="true" />
              {s.label}
            </button>
          );
        })}
        {order.email && (
          <a
            href={`mailto:${order.email}?subject=${encodeURIComponent('Your bracelet order — Whispering Wristwear')}`}
            className="ml-auto inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#354F52] hover:text-[#D4AF37] transition-colors"
            data-testid={`order-email-${order.id}`}
          >
            <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
            Email customer
          </a>
        )}
      </div>
    </>
  );
}

/* ───────── Shared bits ───────── */
function Chip({ active, onClick, label, count, dot, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-widest rounded-full transition-colors ${
        active ? 'bg-[#354F52] text-[#FAF9F6]' : 'bg-white text-[#52796F] hover:bg-[#354F52]/5'
      }`}
      data-testid={testId}
    >
      {dot && <span className="w-2 h-2 rounded-full" style={{ background: dot }} aria-hidden="true" />}
      <span>{label}</span>
      <span className={active ? 'text-[#D4AF37]' : 'text-[#84A98C]'}>{count}</span>
    </button>
  );
}

function EmptyState({ message }) {
  return (
    <div className="bg-white p-10 text-center rounded-sm border border-[#CAD2C5]/40">
      <Package className="w-8 h-8 text-[#84A98C] mx-auto mb-3" strokeWidth={1.25} />
      <p className="text-[#52796F]">{message}</p>
    </div>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <p className="text-caption text-[#84A98C] mb-1">{label}</p>
      <p className="text-sm text-[#354F52] break-words">{children || '—'}</p>
    </div>
  );
}
